import { Component, Prop, h, Element, State, Host, Watch, Event, EventEmitter, Listen } from '@stencil/core';
import { FormValue, PredefinedValues, Root, RowValue, SelectItem } from './types';
import { v4 as uuidv4 } from 'uuid';
import { plus } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-multi-row-connected-dropdown',
  styleUrl: 'pn-multi-row-connected-dropdown.scss',
})
export class PnMultiRowConnectedDropdown {
  @Element() hostElement: Element;

  @Prop() dropdownData: string | Root[] = null;
  @Prop() languageCode: string = null;
  @Prop() label: string = null;
  @Prop() firstDropdownLabel: string = null;
  @Prop() secondDropdownLabel: string = null;
  @Prop() dropdownPlaceholder: string = null;
  @Prop() addRowText: string = null;
  @Prop() inputFieldLabel: string = null;
  @Prop() inputFieldPlaceholder: string = null;
  @Prop() disabledDates: string = null;
  @Prop() dateButtonText: string = null;
  @Prop() datePlaceholder: string = null;
  @Prop() dateDaysFromToday: number = null;
  @Prop() allowMultipleRows: boolean = false;
  @Prop() allowMultipleDates: boolean = false;
  @Prop() startDateLabel: string = null;
  @Prop() endDateLabel: string = null;
  @Prop() multiDateHelperText: string = null;
  @Prop() generateDatesButtonText: string = null;
  @Prop() saveDateButtonText: string = null;
  @Prop() noDatesText: string = null;
  @Prop() yearsToAddToEndDate: number = null;
  @Prop() minAmount: number = 1;
  @Prop() errorMessageEmpty: string;
  @Prop() errorMessageMinAmount: string;
  @Prop() minWarningAmount: number = null;
  @Prop() warningMessageMinAmount: string;

  @State() rowCount: number = null;
  @State() rowData: PredefinedValues[] = null;
  @State() formValues: FormValue[] = null;

  private _buttonValueName: string = null;
  private _firstDropdownValueName: string = null;
  private _secondDropdownValueName: string = null;
  private _inputValueName: string = null;
  private _dateValueName: string = null;

  @Event() allFormValues: EventEmitter<string>;

  private _defaultRow: PredefinedValues = {
    id: this.generateUniqueId(),
    buttons: [],
    firstDropdown: [],
    secondDropdown: [],
    input: '',
    dates: '',
    dateRange: {
      days: [],
      startDate: '',
      endDate: '',
    },
  };

  componentWillLoad() {
    this.setDaysArr();
    this.formValues = [];
    this.rowData = [this._defaultRow];
    this.rowCount = this.rowData.length;

    this._buttonValueName = this.label?.length ? this.label : 'value0';
    this._firstDropdownValueName = this.firstDropdownLabel?.length ? this.firstDropdownLabel : 'value1';
    this._secondDropdownValueName = this.secondDropdownLabel?.length ? this.secondDropdownLabel : 'value2';
    this._inputValueName = this.inputFieldLabel?.length ? this.inputFieldLabel : 'value3';
    this._dateValueName = this.dateButtonText?.length ? this.dateButtonText : 'value4';
  }

  componentDidLoad() {}

  emitValuesIfValid() {
    this.formValues = [];
    this.rowData.forEach(item => {
      const rowValue: FormValue = { row: [] };

      const buttonSelection = item.buttons.find(x => x.selected === true);
      if (buttonSelection) {
        const buttonValue: RowValue = { name: this._buttonValueName, value: buttonSelection.label };
        rowValue.row.push(buttonValue);
      }

      const firstDropdownSelection = item.firstDropdown.find(x => x.selected === true);
      if (firstDropdownSelection) {
        const firstDropdownValue: RowValue = { name: this._firstDropdownValueName, value: firstDropdownSelection.label };
        rowValue.row.push(firstDropdownValue);
      }

      const secondDropdownSelection = item.secondDropdown.find(x => x.selected === true);
      if (secondDropdownSelection) {
        const secondDropdownValue: RowValue = { name: this._secondDropdownValueName, value: secondDropdownSelection.label };
        rowValue.row.push(secondDropdownValue);
      }

      const input = item.input;
      if (input?.length) {
        const inputValue: RowValue = { name: this._inputValueName, value: input };
        rowValue.row.push(inputValue);
      }

      const dates = item.dates;
      if (dates?.length) {
        const datestValue: RowValue = { name: this._dateValueName, value: dates };
        rowValue.row.push(datestValue);
      }

      if (rowValue.row.length === 5) {
        this.formValues.push(rowValue);
      }
    });

    if (this.formValues.length === this.rowData.length) {
      const valueJsonString = JSON.stringify(this.formValues);
      this.allFormValues.emit(valueJsonString);
    } else {
      this.allFormValues.emit('');
    }
  }

  @Listen('rowValueChange')
  predefinedValueChange(e) {
    const indexToUpdate = this.rowData.findIndex(data => data.id === e.detail.id);
    const val: PredefinedValues = e.detail;
    this.rowData[indexToUpdate] = {
      ...this.rowData[indexToUpdate],
      buttons: val.buttons,
      firstDropdown: val.firstDropdown,
      secondDropdown: val.secondDropdown,
      input: val.input,
      dates: val.dates,
      dateRange: val.dateRange,
    };
    this.emitValuesIfValid();
  }

  @Listen('deleteRowEvent')
  deleteRowEvent(e) {
    const indexToRemove = this.rowData.findIndex(data => data.id === e.detail);
    this.rowData.splice(indexToRemove, 1);
    this.rowData = [...this.rowData];
    this.rowCount = this.rowData.length;
    this.emitValuesIfValid();
  }

  generateUniqueId() {
    return uuidv4();
  }

  addRow() {
    const newRow = this._defaultRow;
    newRow.id = this.generateUniqueId();

    this.rowData = [...this.rowData, newRow];
    this.rowCount = this.rowData.length;
  }

  setDaysArr() {
    const language = this.languageCode.toLowerCase();

    switch (language) {
      case 'en':
        this._defaultRow.dateRange.days = [
          { label: 'Monday', value: '1', selected: false },
          { label: 'Tuesday', value: '2', selected: false },
          { label: 'Wednesday', value: '3', selected: false },
          { label: 'Thursday', value: '4', selected: false },
          { label: 'Friday', value: '5', selected: false },
        ];
        break;

      case 'sv':
        this._defaultRow.dateRange.days = [
          { label: 'Måndag', value: '1', selected: false },
          { label: 'Tisdag', value: '2', selected: false },
          { label: 'Onsdag', value: '3', selected: false },
          { label: 'Torsdag', value: '4', selected: false },
          { label: 'Fredag', value: '5', selected: false },
        ];
        break;

      case 'da':
        this._defaultRow.dateRange.days = [
          { label: 'Mandag', value: '1', selected: false },
          { label: 'Tirsdag', value: '2', selected: false },
          { label: 'Onsdag', value: '3', selected: false },
          { label: 'Torsdag', value: '4', selected: false },
          { label: 'Fredag', value: '5', selected: false },
        ];
        break;

      case 'fi':
        this._defaultRow.dateRange.days = [
          { label: 'Maanantai', value: '1', selected: false },
          { label: 'Tiistai', value: '2', selected: false },
          { label: 'Keskiviikko', value: '3', selected: false },
          { label: 'Torstai', value: '4', selected: false },
          { label: 'Perjantai', value: '5', selected: false },
        ];
        break;

      case 'no':
        this._defaultRow.dateRange.days = [
          { label: 'Mandag', value: '1', selected: false },
          { label: 'Tirsdag', value: '2', selected: false },
          { label: 'Onsdag', value: '3', selected: false },
          { label: 'Torsdag', value: '4', selected: false },
          { label: 'Fredag', value: '5', selected: false },
        ];
        break;

      default:
        this._defaultRow.dateRange.days = [
          { label: 'Monday', value: '1', selected: false },
          { label: 'Tuesday', value: '2', selected: false },
          { label: 'Wednesday', value: '3', selected: false },
          { label: 'Thursday', value: '4', selected: false },
          { label: 'Friday', value: '5', selected: false },
        ];
        break;
    }
  }

  render() {
    return (
      <Host>
        {this.rowData?.map((data, i) => {
          return (
            <div class={'generated-row-' + i} id={'generated-row-connected-dropdown-' + i}>
              <pn-multi-row-connected-dropdown-row
                dropdown-data={this.dropdownData}
                language-code={this.languageCode}
                label={this.label}
                first-dropdown-label={this.firstDropdownLabel}
                second-dropdown-label={this.secondDropdownLabel}
                dropdown-placeholder={this.dropdownPlaceholder}
                add-row-text={this.addRowText}
                input-field-label={this.inputFieldLabel}
                input-field-placeholder={this.inputFieldPlaceholder}
                disabled-dates={this.disabledDates}
                date-button-text={this.dateButtonText}
                date-placeholder={this.datePlaceholder}
                date-days-from-today={this.dateDaysFromToday}
                allow-multiple-dates={this.allowMultipleDates}
                start-date-label={this.startDateLabel}
                end-date-label={this.endDateLabel}
                multi-date-helper-text={this.multiDateHelperText}
                generate-dates-button-text={this.generateDatesButtonText}
                save-date-button-text={this.saveDateButtonText}
                no-dates-text={this.noDatesText}
                index={i}
                predefined-value={JSON.stringify(data)}
                years-to-add-to-end-date={this.yearsToAddToEndDate}
                min-amount={this.minAmount}
                error-message-empty={this.errorMessageEmpty}
                error-message-min-amount={this.errorMessageMinAmount}
                min-warning-amount={this.minWarningAmount}
                warning-message-min-amount={this.warningMessageMinAmount}
              ></pn-multi-row-connected-dropdown-row>
            </div>
          );
        })}

        {this.allowMultipleRows ? (
          <div class="connected-dropdown__row">
            <pn-button
              tooltip={this.addRowText ? this.addRowText : null}
              left-icon="true"
              arialabel="Add"
              icon={plus}
              appearance="light"
              variant="borderless"
              class="connected-dropdown__button-right"
              onClick={() => {
                this.addRow();
              }}
            >
              {this.addRowText}
            </pn-button>
          </div>
        ) : null}
      </Host>
    );
  }
}
